import { createFileRoute, Navigate } from '@tanstack/react-router';
import { ConfirmDialog, useUserType } from '@org/ui';
import { trpc, type RouterOutputs } from '@org/clients/school';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { Heart, Trophy, X } from 'lucide-react';

// -- Feminist color palette (local copy — same convention as journey.tsx / mural.tsx) --
const P = {
  maxRed: '#D41920',
  charm: '#F2A0C4',
  baby: '#F8D7DD',
  white: '#FFFFFF',
  cream: '#FFF8F5',
  blush: '#FDF0ED',
  roseMist: '#F9E4E0',
  warmGray: '#756560',
  darkText: '#2D1A1F',
  midText: '#6B4F55',
  lightText: '#8A6F69',
  lightBorder: '#F0DAD5',
  shadow: 'rgba(180, 80, 80, 0.12)',
  shadowDeep: 'rgba(120, 40, 40, 0.18)',
};

const oswald = "'Oswald', sans-serif";
const dmSerif = "'DM Serif Text', serif";

// -- Responsive hook (matches journey.tsx / mural.tsx convention) --
const useWidth = () => {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 640);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
};

// -- Post-it cycle: background + tilt, like the journey wall mini posters --
const CARD_BACKGROUNDS = [P.white, P.baby, P.white, P.roseMist, P.white, P.blush];
const CARD_ROTATIONS = [-1.2, 0.9, -0.7, 1.1, -1.5, 0.6];

const WALL_STYLES = `
.conquistas-card { transition: transform .15s ease, box-shadow .15s ease; break-inside: avoid; }
.conquistas-card:hover { transform: rotate(0deg) translateY(-2px); box-shadow: 6px 10px 22px ${P.shadowDeep}; }
.conquistas-heart { transition: transform .12s ease; }
.conquistas-heart:hover { transform: scale(1.15); }
.conquistas-heart:focus-visible, .conquistas-delete:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.conquistas-btn:hover { filter: brightness(1.06); }
.conquistas-btn:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.conquistas-btn:disabled { opacity: .5; cursor: default; }
.conquistas-textarea:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 1px; }
@media (prefers-reduced-motion: reduce) {
  .conquistas-card, .conquistas-heart { transition: none; }
  .conquistas-card:hover { transform: none; }
  .conquistas-heart:hover { transform: none; }
}
`;

const bodyStyle: CSSProperties = {
  color: P.midText,
  fontSize: 15,
  lineHeight: 1.55,
  margin: 0,
};

const btnRed: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontFamily: oswald,
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  padding: '11px 18px',
  borderRadius: 4,
  border: `2px solid ${P.maxRed}`,
  background: P.maxRed,
  color: P.white,
  lineHeight: 1.2,
  cursor: 'pointer',
};

type WallPost = RouterOutputs['achievements']['list'][number];

export const Route = createFileRoute('/_app/conquistas')({
  component: GatedConquistasRoute,
});

// Woman-cohort wall: b2b (in-company) students are redirected home, same
// pattern as the central route gate (the backend enforces it too).
function GatedConquistasRoute() {
  const { isB2b, isLoading } = useUserType();

  if (isLoading) {
    return null;
  }
  if (isB2b) {
    return <Navigate to="/" replace />;
  }
  return <ConquistasPage />;
}

function PostCard({
  post,
  index,
  dateLocale,
  onDelete,
}: {
  post: WallPost;
  index: number;
  dateLocale: typeof ptBR;
  onDelete: (post: WallPost) => void;
}) {
  const { t } = useTranslation('p2e');
  const utils = trpc.useUtils();
  // Optimistic: flip the heart and the count the moment it's clicked, then
  // let the server answer catch up (same pattern as community's post-card).
  const toggleHeart = trpc.achievements.toggleHeart.useMutation({
    onMutate: async ({ achievementPostId }) => {
      await utils.achievements.list.cancel();
      const previous = utils.achievements.list.getData();
      utils.achievements.list.setData(undefined, (current) =>
        current?.map((p) =>
          p.achievementPostId === achievementPostId
            ? {
                ...p,
                heartedByMe: !p.heartedByMe,
                heartCount: Math.max(0, p.heartCount + (p.heartedByMe ? -1 : 1)),
              }
            : p,
        ),
      );
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) {
        utils.achievements.list.setData(undefined, context.previous);
      }
    },
    onSettled: () => utils.achievements.list.invalidate(),
  });

  const background = CARD_BACKGROUNDS[index % CARD_BACKGROUNDS.length];
  const rotation = CARD_ROTATIONS[index % CARD_ROTATIONS.length];
  const authorName = post.authorName ?? t('achievements.anonymousAuthor');
  const timeAgo = formatDistanceToNow(new Date(post.createdAt), {
    addSuffix: true,
    locale: dateLocale,
  });

  return (
    <article
      className="conquistas-card"
      style={{
        position: 'relative',
        background,
        border: `1px solid ${P.lightBorder}`,
        borderRadius: 8,
        boxShadow: `4px 6px 16px ${P.shadow}`,
        padding: 18,
        marginBottom: 14,
        transform: `rotate(${rotation}deg)`,
      }}
    >
      {/* washi-tape strip, same trick as the journey posters */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: -8,
          left: '50%',
          width: 54,
          height: 16,
          transform: 'translateX(-50%) rotate(-3deg)',
          background: 'rgba(212, 25, 32, 0.12)',
        }}
      />
      {post.canDelete ? (
        <button
          type="button"
          className="conquistas-delete"
          aria-label={t('achievements.deleteAria')}
          onClick={() => onDelete(post)}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: 999,
            border: 'none',
            background: 'transparent',
            color: P.lightText,
            cursor: 'pointer',
          }}
        >
          <X size={14} aria-hidden />
        </button>
      ) : null}

      <p style={{ ...bodyStyle, color: P.darkText, whiteSpace: 'pre-line' }}>{post.body}</p>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginTop: 14,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              fontFamily: oswald,
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: P.maxRed,
              margin: 0,
            }}
          >
            {authorName}
          </p>
          <p style={{ fontSize: 11, color: P.lightText, margin: 0 }}>{timeAgo}</p>
        </div>
        <button
          type="button"
          className="conquistas-heart"
          aria-label={t('achievements.heartAria')}
          aria-pressed={post.heartedByMe}
          disabled={toggleHeart.isPending}
          onClick={() => toggleHeart.mutate({ achievementPostId: post.achievementPostId })}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: P.maxRed,
            fontFamily: oswald,
            fontWeight: 600,
            fontSize: 13,
            padding: 4,
          }}
        >
          <Heart size={18} fill={post.heartedByMe ? P.maxRed : 'none'} aria-hidden />
          {post.heartCount > 0 ? post.heartCount : null}
        </button>
      </div>
    </article>
  );
}

function ConquistasPage() {
  const { t, i18n } = useTranslation('p2e');
  const w = useWidth();
  const isMobile = w < 640;
  const dateLocale = i18n.language?.startsWith('pt') ? ptBR : enUS;

  const utils = trpc.useUtils();
  const { data: posts, isLoading, isError } = trpc.achievements.list.useQuery();

  const [draft, setDraft] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<WallPost | null>(null);

  const createPost = trpc.achievements.create.useMutation({
    onSuccess: () => {
      setDraft('');
      setIsAnonymous(false);
    },
    onSettled: () => utils.achievements.list.invalidate(),
  });
  const removePost = trpc.achievements.remove.useMutation({
    onSettled: () => utils.achievements.list.invalidate(),
  });

  const submit = () => {
    const body = draft.trim();
    if (!body || createPost.isPending) return;
    createPost.mutate({ body, isAnonymous });
  };

  return (
    <div style={{ minHeight: '100vh', background: P.cream, borderRadius: 12 }}>
      <style dangerouslySetInnerHTML={{ __html: WALL_STYLES }} />

      <div style={{ maxWidth: 880, margin: '0 auto', padding: isMobile ? '30px 18px 40px' : '44px 24px 56px' }}>
        <header style={{ marginBottom: 22 }}>
          <p
            style={{
              fontFamily: oswald,
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: P.maxRed,
              margin: 0,
            }}
          >
            {t('achievements.eyebrow')}
          </p>
          <h1
            style={{
              fontFamily: oswald,
              fontSize: isMobile ? 28 : 36,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: P.darkText,
              margin: '4px 0 6px',
              lineHeight: 1.05,
            }}
          >
            {t('achievements.title')}
          </h1>
          <p
            style={{ fontFamily: dmSerif, fontStyle: 'italic', fontSize: isMobile ? 15 : 17, color: P.midText, margin: 0 }}
          >
            {t('achievements.tagline')}
          </p>
        </header>

        {/* Compose card */}
        <section
          aria-label={t('achievements.composeTitle')}
          style={{
            background: P.white,
            border: `1px solid ${P.lightBorder}`,
            borderRadius: 8,
            boxShadow: `6px 10px 22px ${P.shadow}`,
            padding: isMobile ? 16 : 20,
            marginBottom: 28,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Trophy size={18} color={P.maxRed} aria-hidden />
            <h2
              style={{
                fontFamily: oswald,
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: P.darkText,
                margin: 0,
              }}
            >
              {t('achievements.composeTitle')}
            </h2>
          </div>
          <textarea
            className="conquistas-textarea"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('achievements.composePlaceholder')}
            rows={3}
            maxLength={1000}
            style={{
              width: '100%',
              resize: 'vertical',
              background: P.blush,
              border: `1px solid ${P.lightBorder}`,
              borderRadius: 6,
              padding: 12,
              fontSize: 15,
              lineHeight: 1.5,
              color: P.darkText,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginTop: 12,
            }}
          >
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                color: P.midText,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={isAnonymous}
                onChange={(event) => setIsAnonymous(event.target.checked)}
                style={{ accentColor: P.maxRed, width: 16, height: 16 }}
              />
              {t('achievements.composeAnonymous')}
            </label>
            <button
              type="button"
              className="conquistas-btn"
              style={btnRed}
              disabled={!draft.trim() || createPost.isPending}
              onClick={submit}
            >
              {createPost.isPending ? t('achievements.composeSubmitting') : t('achievements.composeSubmit')}
            </button>
          </div>
          {createPost.isError ? (
            <p style={{ ...bodyStyle, color: P.maxRed, marginTop: 8 }}>{t('achievements.composeError')}</p>
          ) : null}
        </section>

        {/* Wall */}
        {isLoading ? (
          <p
            role="status"
            aria-live="polite"
            style={{
              fontFamily: oswald,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              color: P.warmGray,
              textAlign: 'center',
              padding: '40px 0',
            }}
          >
            {t('achievements.loading')}
          </p>
        ) : isError ? (
          <p style={{ ...bodyStyle, textAlign: 'center', padding: '40px 0' }}>{t('achievements.error')}</p>
        ) : !posts || posts.length === 0 ? (
          <p style={{ ...bodyStyle, textAlign: 'center', padding: '40px 0' }}>{t('achievements.empty')}</p>
        ) : (
          <div style={{ columnCount: isMobile ? 1 : 2, columnGap: 16 }}>
            {posts.map((post, index) => (
              <PostCard
                key={post.achievementPostId}
                post={post}
                index={index}
                dateLocale={dateLocale}
                onDelete={setPendingDelete}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        onConfirm={() => {
          if (pendingDelete) {
            removePost.mutate({ achievementPostId: pendingDelete.achievementPostId });
          }
          setPendingDelete(null);
        }}
        title={t('achievements.deleteTitle')}
        description={t('achievements.deleteDescription')}
        confirmLabel={t('achievements.deleteConfirm')}
        cancelLabel={t('achievements.deleteCancel')}
        destructive
      />
    </div>
  );
}
